/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { useCallback, useEffect, useState } from "react";
import { MdArrowDropDown } from "react-icons/md";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import axiosWrapper from "@Services/axiosWrapper";
import type {
  GetSelectedViewerLayoutResponse,
  GetViewDataResponse,
  LayoutData,
  SetSelectedViewerLayoutResponse,
} from "@ServerTypes/layoutView/layoutView.schema";
import { setModalStatus, setViewerState } from "@Reducers/Viewer";
import DesignViewerLayoutSetting from "../DesignViewerLayoutSetting";
import type { HoopsViewerHandle, RcDockLayout, RcDockNode, ViewerLayout } from "../DesignViewerLayoutSetting";
import type { SidebarGroup } from "../DesignViewerLayoutSetting/SettingComponent";
import type { ModalEntry } from "@Reducers/Viewer";
import useModulesMapper from "@Components/StickySidebarViewer/ModulesMapper";
import { removingElementFromRcDock } from "@Services/utils/helpers";
import Views from "@Components/Views";
import _ from "lodash";

import { QueryBuilder, PredicateBuilder } from "@Services/queryBuilder";
import { Box } from "@mui/material";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_SUCCESS } from "@Services/constants";
import { useTranslation } from "@i18n";
import { RootState } from "@Reducers/index";

/**
 * The members this file reads off ModulesMapper's dictionary. `useModulesMapper`
 * (`@Components/StickySidebarViewer`, outside this module) declares its mapper `any`, so the shape
 * is adopted with one erased assertion at the hook call below rather than by editing that hook.
 */
export type SidebarModuleEntry = { initialState?: ModalEntry; isVisible?: boolean; belongToGroup?: string };

/** The `viewerState` fields this component dispatches; the slice itself is open-ended. */
type ViewerLayoutState = {
  zoomLevel?: number | null;
  layoutRcDockData?: RcDockLayout | false;
  sidebarData?: string[];
};

/**
 * SCHEMA GAP: `layoutDataSchema.data` is the opaque `Record<string, unknown>` blob, so a stored row
 * only IS a `ViewerLayout` once the two members `setLayoutDataInDesignViewer` dereferences unguarded
 * (`data.rcDock` and `data.sidebarLayout.addedData`) are actually there. Rows that fail the check -
 * including the `null` getSelectedViewerLayout returns when nothing is pinned - are skipped instead
 * of being asserted into the shape and then crashing on the first read.
 */
function isViewerLayout(row: LayoutData | null | undefined): row is ViewerLayout {
  const blob = row?.data;
  if (!blob) return false;
  const { rcDock, sidebarLayout } = blob;
  return (
    typeof rcDock === "object" &&
    rcDock !== null &&
    typeof sidebarLayout === "object" &&
    sidebarLayout !== null &&
    "addedData" in sidebarLayout &&
    Array.isArray(sidebarLayout.addedData)
  );
}

interface DesignViewerLayoutManagerProps {
  HoopsViewer: HoopsViewerHandle;
  modalToOpen?: string[];
  fileVersionId: string;
}

function DesignViewerLayoutManager({ HoopsViewer, modalToOpen = [], fileVersionId }: DesignViewerLayoutManagerProps) {
  const dispatch = useDispatch();
  const layout = useSelector((state: RootState) => state.layout);
  const [LayoutDropdown, setLayoutDropdown] = useState<boolean>(false);
  const [availableData, setAvailableData] = useState<SidebarGroup[]>([]);
  const [addedData, setAddedData] = useState<SidebarGroup[]>([]);
  const [layoutSettingMenu, setLayoutSettingMenu] = useState<boolean>(false);
  // Both are null until a real row has been assigned: `updateData` by `onEditCallback` (which is
  // also what opens the settings modal) and `defaultLayout` by the `isDefault` row of getViewData.
  const [updateData, setUpdateData] = useState<ViewerLayout | null>(null);

  const [currentLayoutData, setcurrentLayoutData] = useState<ViewerLayout | null>(null);
  const [layoutData, setLayoutData] = useState<LayoutData[]>([]);
  const [defaultLayout, setDefaultLayout] = useState<ViewerLayout | null>(null);
  const urlSearchParams = new URLSearchParams(location.search);

  const HoopsModalStatus = useSelector((state: RootState) => state.HoopsModalStatus);
  const HoopsModalStatusClone = _.cloneDeep(HoopsModalStatus);
  // `useModulesMapper` returns the heterogeneous ARRAY `[mapper, handleToolClick]`, so index 0 types
  // as `mapper | handler`. Removing this assertion needs `as const` (or an explicit tuple return
  // type) on that hook's return statement in @Components/StickySidebarViewer/ModulesMapper - nothing
  // in this file can recover the tuple.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- hook returns an array literal, not a tuple; see comment above
  const [modulesMapper] = useModulesMapper({
    estimateId: !!urlSearchParams.get("estimateId"),
    fileVersionId,
  }) as [Record<string, SidebarModuleEntry>];
  const { t } = useTranslation();

  const getSelectedLayout = useCallback(async () => {
    const selectedViewerLayout = await axiosWrapper.post<GetSelectedViewerLayoutResponse>(
      `${getBackendUrl()}/layoutView/getSelectedViewerLayout`,
      {
        key: "VIEWER_SELECTED_LAYOUT",
      },
    );
    // `data` is null both when no selection is stored and when the pinned view is no longer visible,
    // and its blob is the opaque record - so the row is CHECKED, never asserted, before it is stored.
    const selectedRow = selectedViewerLayout.data.data;
    if (isViewerLayout(selectedRow)) {
      setcurrentLayoutData(selectedRow);
    }
  }, []);

  const init = useCallback(async () => {
    await getSelectedLayout();
  }, [getSelectedLayout]);

  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    async function save() {
      if (LayoutDropdown) {
        await axiosWrapper.post<SetSelectedViewerLayoutResponse>(
          `${getBackendUrl()}/layoutView/setSelectedViewerLayout`,
          {
            selectedViewerId: currentLayoutData!._id,
            key: "VIEWER_SELECTED_LAYOUT",
          },
        );
      }
      setLayoutDataInDesignViewer(currentLayoutData!);
    }
    if (currentLayoutData) {
      save();
    }
  }, [currentLayoutData]);

  useEffect(() => {
    if (LayoutDropdown) {
      getLayoutData();
    }
  }, [LayoutDropdown]);

  async function getLayoutData() {
    const query1 = new QueryBuilder().where(new PredicateBuilder("resourceType", "equal", "VIEWER"));
    const layoutResponse = await axiosWrapper.post<GetViewDataResponse>(`${getBackendUrl()}/layoutView/getViewData`, {
      value: query1.build(),
    });
    // `data` is optional on the response schema (it is absent only on a failed lookup) and is
    // dereferenced unguarded here; assertions are erased, a guard would change behaviour.
    setLayoutData(layoutResponse.data.data!);

    layoutResponse.data.data!.map((data: LayoutData) => {
      if (data.isDefault && isViewerLayout(data)) {
        setDefaultLayout(data);
      }
    });
  }

  async function setLayoutDataInDesignViewer(viewerData: ViewerLayout) {
    const viewerStateObject: ViewerLayoutState = {};
    let sidebar: string[];
    let modalStatus: Record<string, ModalEntry> = {};
    const activeIds: string[] = [];
    const nonActiveIds: string[] = [];
    if (viewerData.isDefault) {
      viewerStateObject.zoomLevel = 100;
      viewerStateObject.layoutRcDockData = false;
      // `MODULES_ENABLED` is optional on the portal layout schema; no enabled modules means an empty
      // sidebar, which is what the two `?? -1` reads of the same field below already assume.
      sidebar = makeSidebarDataSequenceWise(layout.DESIGNVIEWER?.MODULES_ENABLED ?? []);
      const hoopsStatuses = { ...HoopsModalStatusClone };
      Object.keys(hoopsStatuses).map((data: string) => {
        if (data != "Viewer") {
          const entry = hoopsStatuses[data];
          if (entry) {
            entry.show = false;
          }
        }
      });
      modalStatus = hoopsStatuses;
    } else {
      sidebar = makeSidebarDataSequenceWise(viewerData.data.sidebarLayout.addedData);
      viewerStateObject.zoomLevel = viewerData.data.zoomLevel;
      viewerStateObject.layoutRcDockData = viewerData.data.rcDock;
      const rcDock: RcDockLayout = JSON.parse(JSON.stringify(viewerData.data.rcDock));
      Object.keys(rcDock).map((key: string) => {
        if (rcDock[key]!.children.length > 0) {
          rcDock[key]!.children.map((child: RcDockNode) => {
            recusiveLoopForActiveSidebarInLayout(child, activeIds, nonActiveIds);
          });
        }
      });
      if (nonActiveIds.length > 0) {
        Object.keys(rcDock).map((key: string) => {
          if (rcDock[key]!.children.length > 0) {
            rcDock[key]!.children = rcDock[key]!.children.filter((child: RcDockNode) =>
              removingElementFromRcDock(child, nonActiveIds),
            );
          }
        });
      }
      viewerStateObject.layoutRcDockData = rcDock;
      activeIds.forEach((dock: string) => {
        const initializeState = modulesMapper[dock]?.initialState || {};
        modalStatus[dock] = {
          show: true,
          ...initializeState,
        };
      });
      const hoopsStatuses = { ...HoopsModalStatusClone };
      Object.keys(hoopsStatuses).map((data: string) => {
        if (data != "Viewer") {
          const entry = hoopsStatuses[data];
          if (entry) {
            entry.show = false;
          }
        }
      });
      modalStatus = { ...hoopsStatuses, ...modalStatus };
    }
    viewerStateObject.sidebarData = sidebarFilterWithModulesEnabled(sidebar);
    if (modalToOpen.length > 0) {
      let filteredModalToOpen = modalToOpen.filter(
        (data) => !activeIds.includes(data) && modulesMapper[data]?.isVisible,
      );
      for (const dock of filteredModalToOpen) {
        const initializeState = modulesMapper[dock]?.initialState || {};
        modalStatus[dock] = {
          show: true,
          ...initializeState,
        };

        if (dock === "Issues") {
          modalStatus[dock]!.currentStage = "view_issue";
        }
      }
    }

    dispatch(setViewerState({ ...viewerStateObject, modalToOpen }));
    dispatch(setModalStatus(modalStatus));
  }

  function sidebarFilterWithModulesEnabled(layoutData: string[]) {
    return layoutData.filter((e: string) => (layout.DESIGNVIEWER?.MODULES_ENABLED?.indexOf(e) ?? -1) > -1);
  }

  function makeSidebarDataSequenceWise(sidebar: string[]) {
    const sidebarAsPerRelation = sidebarGrouping(sidebar);
    const result: string[] = [];
    Object.keys(sidebarAsPerRelation).map((data: string) => {
      result.push(...sidebarAsPerRelation[data]!);
    });
    return result;
  }

  function recusiveLoopForActiveSidebarInLayout(child: RcDockNode, activeIds: string[], nonActiveIds: string[]) {
    // `undefined > 0` is `false`, which is the intent; the assertion only silences the comparison.
    if (child.children?.length! > 0) {
      child.children!.map((child: RcDockNode) => {
        recusiveLoopForActiveSidebarInLayout(child, activeIds, nonActiveIds);
      });
    }
    if (child.tabs) {
      child.tabs.map((tab: { id: string }) => {
        if (!modulesMapper[tab.id] || modulesMapper[tab.id]?.isVisible) {
          activeIds.push(tab.id);
        } else {
          nonActiveIds.push(tab.id);
        }
      });
    }
  }

  function sidebarGrouping(sidebar: string[], dataToAvoid: string[] = []) {
    // Only ever keyed by the group NAME (never by index) and only ever read back through
    // `Object.keys`, so this is a dictionary - it used to be an empty ARRAY carrying string props,
    // which enumerates identically but could not be typed without an assertion.
    const sidebarAsPerRelation: Record<string, string[]> = {};
    sidebar.map((data: string) => {
      if (
        !dataToAvoid.includes(data) &&
        (layout.DESIGNVIEWER?.MODULES_ENABLED?.indexOf(data) ?? -1) > -1 &&
        data != "Divider"
      ) {
        // A module with no `belongToGroup` is keyed under the string "undefined"; preserved.
        if (sidebarAsPerRelation[modulesMapper[data]?.belongToGroup!]) {
          sidebarAsPerRelation[modulesMapper[data]?.belongToGroup!] = [
            ...sidebarAsPerRelation[modulesMapper[data]?.belongToGroup!]!,
            data,
          ];
        } else {
          sidebarAsPerRelation[modulesMapper[data]?.belongToGroup!] = [data];
        }
      }
    });
    return sidebarAsPerRelation;
  }
  function makeDnDComponentData({
    addedData,
    makeAddedData,
    makeAvailableData,
  }: {
    addedData: string[];
    makeAddedData: boolean;
    makeAvailableData: boolean;
  }) {
    const dataToAvoid: string[] = [];
    if (makeAvailableData) {
      dataToAvoid.push(...addedData);
    }
    const portalAvailableSidebarOption = makeAddedData ? addedData : (layout.DESIGNVIEWER?.MODULES_ENABLED ?? []);
    const sidebarAsPerRelation = sidebarGrouping(portalAvailableSidebarOption, dataToAvoid);
    const result: SidebarGroup[] = [];
    Object.keys(sidebarAsPerRelation).map((data: string) => {
      result.push({
        [data]: {
          widgets: sidebarAsPerRelation[data]!,
        },
      });
    });
    return result;
  }

  function createNewLayoutData() {
    const defaultAddedData = layout.DESIGNVIEWER?.MODULES_ENABLED;
    const newLayout = HoopsViewer.rcDockref.saveLayout();
    return {
      rcDock: { ...JSON.parse(JSON.stringify(newLayout)) },
      sidebarLayout: {
        addedData: defaultAddedData,
      },
      zoomLevel: 100,
    };
  }

  async function onClickLayout(clickLayout: ViewerLayout) {
    setcurrentLayoutData(clickLayout);
  }

  async function onDuplicateCallback({ duplicatedData }: { duplicatedData: LayoutData }) {
    dispatch(
      showToastNotification({
        content: t("designviewer_layout_duplicated", { layoutName: duplicatedData.name }),
        type: TOAST_TYPE_SUCCESS,
      }),
    );
  }

  async function onRenameCallback({ renameData }: { renameData: ViewerLayout }) {
    dispatch(
      showToastNotification({
        content: t("designviewer_layout_renamed", { layoutName: renameData.name }),
        type: TOAST_TYPE_SUCCESS,
      }),
    );
    if (renameData._id == currentLayoutData!._id) {
      setcurrentLayoutData(renameData);
    }
  }

  async function onEditCallback({
    layoutData,
    layoutDataIndex,
  }: {
    layoutData: ViewerLayout;
    layoutDataIndex: number;
  }) {
    setUpdateData({ ...layoutData, layoutDataIndex });
    setLayoutSettingMenu(true);
    const addedData = makeDnDComponentData({
      addedData: layoutData.data.sidebarLayout.addedData,
      makeAddedData: true,
      makeAvailableData: false,
    });
    setAddedData(addedData);
    const availableData = makeDnDComponentData({
      addedData: layoutData.data.sidebarLayout.addedData,
      makeAddedData: false,
      makeAvailableData: true,
    });
    setAvailableData(availableData);
  }

  async function onSaveSettingCallback({ updateData }: { updateData: ViewerLayout }) {
    setLayoutData((prevState) => {
      const tempData = prevState;
      // `layoutDataIndex` is stamped by `onEditCallback` before the settings modal can save.
      tempData[updateData.layoutDataIndex!] = updateData;
      return tempData;
    });
    dispatch(
      showToastNotification({
        content: t("designviewer_layout_updated", { layoutName: updateData.name }),
        type: TOAST_TYPE_SUCCESS,
      }),
    );
    if (updateData._id == currentLayoutData!._id) {
      setcurrentLayoutData({ ...updateData });
    }
  }

  async function onDeleteCallback({ deleteData }: { deleteData: LayoutData }) {
    dispatch(
      showToastNotification({
        content: t("designviewer_layout_deleted", { layoutName: deleteData.name }),
        type: TOAST_TYPE_SUCCESS,
      }),
    );
    if (deleteData._id == currentLayoutData!._id) {
      setcurrentLayoutData(defaultLayout);
    }
  }

  return (
    <>
      <div
        className="layoutManagerRoot preventLayoutClose relative flex items-center ps-3.5 pe-3.5 font-semibold"
        data-testid="layoutManagerRoot"
        id="layoutDropDown"
      >
        <Box
          onClick={() => setLayoutDropdown(!LayoutDropdown)}
          className="flex items-center cursor-pointer"
          data-testid="layoutDropdown"
        >
          <div className="overflow-hidden whitespace-nowrap text-ellipsis max-w-40" data-testid="currentLayoutDataName">
            {currentLayoutData?.name}
          </div>
          <MdArrowDropDown size="1.5rem" />
        </Box>
        {LayoutDropdown && (
          <div
            className="absolute top-8 start-0 flex w-[27.5rem] flex-col justify-between overflow-auto rounded-lg bg-white p-6 text-[var(--primary-text)] shadow-[0.25rem_0.25rem_0.9375rem_0_rgba(65,92,171,0.15)]"
            data-testid="layoutDropdownContent"
          >
            <Views
              layoutData={layoutData}
              setLayoutData={setLayoutData}
              currentDataId={_.get(currentLayoutData, "_id")}
              onClickLayout={onClickLayout}
              onRenameCallback={onRenameCallback}
              onEditCallback={onEditCallback}
              onDeleteCallback={onDeleteCallback}
              createNewLayoutData={createNewLayoutData}
              enableUpdate
              name="Layout"
              onDuplicateCallback={onDuplicateCallback}
            />
          </div>
        )}
      </div>
      {layoutSettingMenu && updateData && (
        <DesignViewerLayoutSetting
          updateData={updateData}
          setLayoutSettingMenu={setLayoutSettingMenu}
          availableData={availableData}
          setAvailableData={setAvailableData}
          addedData={addedData}
          setAddedData={setAddedData}
          HoopsViewer={HoopsViewer}
          setUpdateData={setUpdateData}
          onSaveCallback={onSaveSettingCallback}
        />
      )}
    </>
  );
}

export default DesignViewerLayoutManager;
